import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import blogArticles from '../data/blogArticles';

const BlogArticle: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const article = blogArticles.find(a => a.slug === slug);

  useEffect(() => {
    if (article) {
      // Update page title and meta description
      document.title = `${article.meta_title_en || article.title_en} | Farsi Learning App`;
      
      // Update meta description
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', article.meta_description_en || article.summary_en);
      }

      // Add structured data for SEO
      const structuredData = {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": article.title_en,
        "description": article.summary_en,
        "image": article.featured_image,
        "author": {
          "@type": "Person",
          "name": article.author_name
        },
        "publisher": {
          "@type": "Organization",
          "name": "Farsi Learning App",
          "url": "https://farsi-app.com"
        },
        "datePublished": article.published_date,
        "dateModified": article.published_date,
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": article.canonical_url
        },
        "articleSection": article.category,
        "keywords": article.tags.join(", "),
        "inLanguage": article.language === "bilingual" ? ["en", "fa"] : "en"
      };

      // Add structured data script to head
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(structuredData);
      document.head.appendChild(script);

      // Cleanup function
      return () => {
        document.head.removeChild(script);
      };
    }
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50" dir="ltr">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Article Not Found</h1>
        <p className="text-gray-700 mb-8">Sorry, we couldn't find the article you're looking for.</p>
        <Link to="/blog" className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition">Back to Blog</Link>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen" dir="ltr">
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-6 flex items-center">
          <Link to="/blog" className="text-purple-600 hover:text-purple-800 font-semibold mr-4">&larr; Back to Blog</Link>
          <h1 className="text-2xl font-bold text-gray-900">{article.title_en}</h1>
        </div>
      </header>
      <main className="container mx-auto px-4 py-12 max-w-3xl">
        <img src={article.featured_image} alt={article.title_en} className="w-full rounded-xl mb-6 shadow-md" />
        <h2 className="text-3xl font-bold text-gray-900 mb-2">{article.title_en}</h2>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center text-sm text-gray-500">
            <span>By {article.author_name}</span>
            <span className="mx-2">•</span>
            <span>{new Date(article.published_date).toLocaleDateString()}</span>
            <span className="mx-2">•</span>
            <span>{article.reading_time} min read</span>
          </div>
          
          {/* Social Share Buttons */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500 mr-2">Share:</span>
            <button
              onClick={() => {
                const url = encodeURIComponent(window.location.href);
                const text = encodeURIComponent(`Check out this article about ${article.title_en}`);
                window.open(`https://twitter.com/intent/tweet?url=${url}&text=${text}`, '_blank');
              }}
              className="p-2 text-gray-400 hover:text-blue-500 transition-colors"
              title="Share on Twitter"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
              </svg>
            </button>
            <button
              onClick={() => {
                const url = encodeURIComponent(window.location.href);
                window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank');
              }}
              className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
              title="Share on Facebook"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </button>
            <button
              onClick={() => {
                navigator.clipboard.writeText(window.location.href);
                alert('Link copied to clipboard!');
              }}
              className="p-2 text-gray-400 hover:text-green-600 transition-colors"
              title="Copy link"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </button>
          </div>
        </div>
        <p className="text-lg text-gray-700 mb-8">{article.summary_en}</p>
        
        {/* Render content sections if available */}
        {article.content_sections && article.content_sections.map((section, index: number) => (
          <div key={index} className="mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">{section.title}</h3>
            {section.content && (
              <div className="text-gray-700 mb-4 leading-relaxed">
                {section.content.split('\n\n').map((paragraph, pIndex) => (
                  paragraph.trim() && (
                    <p key={pIndex} className="mb-4 last:mb-0">
                      {paragraph.trim()}
                    </p>
                  )
                ))}
              </div>
            )}
            {section.phrases && (
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-6 mb-6">
                <div className="grid gap-4">
                  {section.phrases.map((phrase, phraseIndex: number) => (
                    <div key={phraseIndex} className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                      <div className="flex flex-col md:flex-row md:items-center gap-3">
                        <div className="flex-1">
                          <p className="text-2xl font-bold text-gray-900 mb-1" dir="rtl">{phrase.persian}</p>
                          <p className="text-lg text-purple-600 font-medium mb-1">{phrase.pronunciation}</p>
                          <p className="text-gray-700">{phrase.english}</p>
                        </div>
                        <div className="md:flex-shrink-0">
                          <span className="inline-block bg-gray-100 text-gray-600 text-xs px-3 py-1 rounded-full">
                            {phrase.context}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {section.dish_details && (
              <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-lg p-6 mb-6 border border-orange-100">
                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <div className="flex flex-col lg:flex-row gap-6">
                    <div className="flex-1">
                      <h4 className="text-xl font-bold text-gray-900 mb-3">Dish Information</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <span className="font-semibold text-gray-700">Persian Name:</span>
                          <p className="text-2xl font-bold text-gray-900 mt-1" dir="rtl">{section.dish_details.persian_name}</p>
                          <p className="text-sm text-gray-600">({section.dish_details.pronunciation})</p>
                        </div>
                        <div>
                          <span className="font-semibold text-gray-700">Origin:</span>
                          <p className="text-gray-900 mt-1">{section.dish_details.origin}</p>
                        </div>
                        <div>
                          <span className="font-semibold text-gray-700">Cooking Time:</span>
                          <p className="text-gray-900 mt-1">{section.dish_details.cooking_time}</p>
                        </div>
                        <div>
                          <span className="font-semibold text-gray-700">Difficulty:</span>
                          <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium mt-1 ${
                            section.dish_details.difficulty === 'Beginner' ? 'bg-green-100 text-green-800' :
                            section.dish_details.difficulty === 'Intermediate' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {section.dish_details.difficulty}
                          </span>
                        </div>
                      </div>
                      <div className="mt-4">
                        <span className="font-semibold text-gray-700">Flavor Profile:</span>
                        <p className="text-gray-900 mt-1">{section.dish_details.flavor_profile}</p>
                      </div>
                    </div>
                    <div className="lg:w-1/3">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h5 className="font-semibold text-gray-700 mb-3">Main Ingredients</h5>
                        <ul className="space-y-1">
                          {section.dish_details.main_ingredients.map((ingredient: string, idx: number) => (
                            <li key={idx} className="text-sm text-gray-600 flex items-center">
                              <span className="w-2 h-2 bg-orange-400 rounded-full mr-2"></span>
                              {ingredient}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <span className="font-semibold text-gray-700">Cultural Significance:</span>
                    <p className="text-gray-700 mt-1 italic">{section.dish_details.cultural_significance}</p>
                  </div>
                </div>
              </div>
            )}
            {section.poetry_examples && (
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-6 mb-6">
                <h4 className="text-xl font-bold text-gray-900 mb-4">Poetry Examples</h4>
                {section.poetry_examples.map((example, exampleIndex: number) => (
                  <div key={exampleIndex} className="bg-white rounded-lg p-4 shadow-sm border border-gray-100 mb-4">
                    <h5 className="font-semibold text-gray-800 mb-3">{example.title}</h5>
                    <div className="space-y-2">
                      <p className="text-2xl font-bold text-gray-900" dir="rtl">{example.persian}</p>
                      <p className="text-lg text-purple-600 font-medium">{example.transliteration}</p>
                      <p className="text-gray-700">{example.english}</p>
                      <p className="text-sm text-gray-600 italic">{example.context}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {section.vocabulary_section && (
              <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-6 mb-6">
                <h4 className="text-xl font-bold text-gray-900 mb-4">{section.vocabulary_section.title}</h4>
                <div className="grid gap-4">
                  {section.vocabulary_section.words.map((word: any, wordIndex: number) => (
                    <div key={wordIndex} className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                      <div className="flex flex-col md:flex-row md:items-center gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <p className="text-2xl font-bold text-gray-900" dir="rtl">{word.persian}</p>
                            <p className="text-lg text-purple-600 font-medium">({word.transliteration})</p>
                          </div>
                          <p className="text-gray-900 font-semibold mb-1">{word.english}</p>
                          <p className="text-gray-600 text-sm">{word.definition}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {(section as any).poetry_analysis && (
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg p-6 mb-6">
                <h4 className="text-xl font-bold text-gray-900 mb-4">{(section as any).poetry_analysis.ghazal_title}</h4>
                <div className="space-y-4">
                  {(section as any).poetry_analysis.lines.map((line: any, lineIndex: number) => (
                    <div key={lineIndex} className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                      <div className="space-y-2">
                        <p className="text-2xl font-bold text-gray-900" dir="rtl">{line.persian}</p>
                        <p className="text-lg text-purple-600 font-medium">{line.transliteration}</p>
                        <p className="text-gray-700">{line.english}</p>
                        <div className="border-t border-gray-200 pt-2 mt-2">
                          <p className="text-sm text-gray-600"><strong>Analysis:</strong> {line.analysis}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="bg-gray-50 rounded-lg p-4 mt-4">
                    <p className="text-sm text-gray-700"><strong>Themes:</strong> {section.poetry_analysis.themes}</p>
                  </div>
                </div>
              </div>
            )}
            {section.cultural_practices && (
              <div className="bg-gradient-to-r from-pink-50 to-rose-50 rounded-lg p-6 mb-6">
                <h4 className="text-xl font-bold text-gray-900 mb-4">Cultural Practices</h4>
                <div className="grid gap-4">
                  {section.cultural_practices.map((practice: any, practiceIndex: number) => (
                    <div key={practiceIndex} className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                      <h5 className="font-semibold text-gray-800 mb-2">{practice.practice}</h5>
                      <p className="text-gray-700 mb-2">{practice.description}</p>
                      <p className="text-sm text-gray-600 italic"><strong>Cultural Significance:</strong> {practice.cultural_significance}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {section.reading_tips && (
              <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg p-6 mb-6">
                <h4 className="text-xl font-bold text-gray-900 mb-4">Reading Tips</h4>
                <ul className="space-y-2">
                  {section.reading_tips.map((tip: string, tipIndex: number) => (
                    <li key={tipIndex} className="flex items-start">
                      <span className="w-2 h-2 bg-indigo-400 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                      <span className="text-gray-700">{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {section.language_learning_benefits && (
              <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-lg p-6 mb-6">
                <h4 className="text-xl font-bold text-gray-900 mb-4">Language Learning Benefits</h4>
                <ul className="space-y-2">
                  {section.language_learning_benefits.map((benefit: string, benefitIndex: number) => (
                    <li key={benefitIndex} className="flex items-start">
                      <span className="w-2 h-2 bg-teal-400 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                      <span className="text-gray-700">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
        
        {/* Interactive Call-to-Action for Hafez Article */}
        {article.slug === 'poetry-of-hafez-intro' && (
          <div className="mt-12 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-2xl p-8 text-white">
            <div className="text-center">
              <h3 className="text-2xl font-bold mb-4">Ready to Explore Hafez's Poetry?</h3>
              <p className="text-lg mb-6 text-purple-100">
                Dive deeper into Persian literature with our interactive Hafez lessons
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/hafez"
                  className="bg-white text-purple-600 px-6 py-3 rounded-lg font-semibold hover:bg-purple-50 transition-colors"
                >
                  Start Hafez Lessons
                </Link>
                <Link
                  to="/study"
                  className="border border-white text-white px-6 py-3 rounded-lg font-semibold hover:bg-white hover:text-purple-600 transition-colors"
                >
                  Learn Persian Vocabulary
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Quote Section for Poetry Articles */}
        {article.category === 'Literature' && (
          <div className="mt-8 bg-gray-50 rounded-lg p-6 border-l-4 border-purple-500">
            <blockquote className="text-lg italic text-gray-700 mb-4" dir="rtl">
              "الا یا ایها الساقی ادر کاسا و ناولها"
            </blockquote>
            <p className="text-purple-600 font-medium">
              "O cupbearer, bring wine and pass the cup around" - Hafez
            </p>
            <p className="text-sm text-gray-600 mt-2">
              Experience the beauty of Persian poetry in its original form while learning the language.
            </p>
          </div>
        )}

        <div className="mt-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Tags:</h3>
          <div className="flex flex-wrap gap-2">
            {article.tags.map(tag => (
              <span key={tag} className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">{tag}</span>
            ))}
          </div>
        </div>
        {/* Newsletter Signup Section */}
        <div className="mt-12 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-8 border border-indigo-100">
          <div className="text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">📚 Love Persian Literature?</h3>
            <p className="text-lg text-gray-700 mb-6">
              Get weekly insights into Persian poetry, culture, and language learning tips delivered to your inbox.
            </p>
            <div className="max-w-md mx-auto">
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="flex-1 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <button className="bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors">
                  Subscribe
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                No spam, unsubscribe at any time. Join 2,500+ Persian learners.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Related Articles:</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {article.related_articles.map(slug => {
              const related = blogArticles.find(a => a.slug === slug);
              return related ? (
                <Link
                  key={slug}
                  to={`/blog/${related.slug}`}
                  className="block p-4 bg-white rounded-lg border border-gray-200 hover:border-purple-300 hover:shadow-md transition-all"
                >
                  <h4 className="font-semibold text-gray-900 mb-2 line-clamp-2">{related.title_en}</h4>
                  <p className="text-sm text-gray-600 line-clamp-2">{related.summary_en}</p>
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-xs text-purple-600 font-medium">{related.category}</span>
                    <span className="text-xs text-gray-500">{related.reading_time} min</span>
                  </div>
                </Link>
              ) : null;
            })}
          </div>
        </div>
      </main>
    </div>
  );
};

export default BlogArticle; 